import { Injectable } from '@angular/core';
import { Observable, from, map, of, switchMap, finalize, shareReplay } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import OktaAuth from '@okta/okta-auth-js';
import { OKTAConfig } from './okta.config';
import { AppService } from '../../../../app.service';

// Refresh the access-token when it has 5 minutes or less of remaining lifetime.
export const OKTA_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

@Injectable()
export class OktaService {

  private get url(): {
    refreshToken: string,
    accessToken: string,
  } {
    return this.appService.getApiUrls().okta;
  }

  private oktaAuth;
  private config;
  private refreshInFlight$: Observable<any> | null = null;

  constructor(protected appService: AppService,
    protected httpClient: HttpClient) {
  }

  public getConfig() {
    return this.config;
  }

  public initOktaAuth(okta: any) {
    this.config =  Object.assign({}, OKTAConfig, okta);
    this.oktaAuth = new OktaAuth({
      pkce:  false,
      clientId: this.config.clientId,
      issuer: this.config.issuer,
      redirectUri: this.config.redirectUri,
    });
  }

  async signIn() {
		await this.oktaAuth.token.getWithRedirect({ scopes: this.oktaAuth.options.scopes, responseType: ['token', 'id_token'] });
	}

  public goToLoginPage(username?: string) {
    const { scope } = this.getConfig();

    this.oktaAuth.token.getWithRedirect({
      scopes: scope.split(' '),
      responseType: ['id_token', 'token', 'code'],
      loginHint: username,
    });
  }

  public async logout() {
    await this.oktaAuth.tokenManager.clear();
    return await this.oktaAuth.signOut();
  }

  public async handleAuthentication() {
    const auth = await this.oktaAuth.token.parseFromUrl();
    if(auth?.tokens){
      this.oktaAuth.tokenManager.add('idToken', auth.tokens.idToken);
      this.oktaAuth.tokenManager.add('accessToken',  auth.tokens.accessToken);
    }
  }

  public isAuthenticated(): Observable<any> {
    const token =  this.getAccessToken();
    return from(token);
  }

  public async isImpersonated() {
    const token = await this.getAccessToken();
    const { impersonation } = OktaService.parseToken(token.accessToken);

    return !!impersonation;
  }

  public async isTokenExpired() {
    const token = await this.getAccessToken();
    if (!token) return true;
    return token.expiresAt * 1000 < Date.now();
  }

  public async getAccessToken() {
    const data = await this.oktaAuth?.tokenManager.get('accessToken');
    return data;
  }

  public getOktaRefreshToken(params): Observable<any> {
    return this.httpClient.get(this.url.refreshToken, {
      responseType: 'text',
      params: this.getHttpParams(params)
    }).pipe(map((token) => JSON.parse(token)));
  }

  public refreshOktaAccessToken(): Observable<any> {
    const config = this.getConfig();
    const refreshToken = localStorage.getItem('okta-refresh-token');
    if(refreshToken == null){
      return of(null);
    }
    const params = {
      refreshToken: refreshToken,
      scopes: encodeURIComponent(config.scope),
    };

    return this.httpClient.get(this.url.accessToken, {
      responseType: 'text',
      params: this.getHttpParams(params)
    }).pipe(map((newToken) => {
      const tokens = JSON.parse(localStorage.getItem('okta-token-storage'));
      const parsedToken = JSON.parse(newToken);

      if (!tokens?.accessToken) {
        return null;
      }

      tokens.accessToken.accessToken = parsedToken;
      this.oktaAuth.tokenManager.add('accessToken', tokens.accessToken);

      return OktaService.parseToken(parsedToken);
    }));
  }

  /**
   * Ensures the cached access-token has more than OKTA_REFRESH_THRESHOLD_MS of
   * remaining lifetime. If it does not, performs a refresh via the OTS
   * refresh-token endpoint and returns the resulting token payload. Concurrent
   * callers share a single in-flight refresh request.
   *
   * Emits `null` when there is no access-token to refresh (unauthenticated).
   */
  public ensureValidAccessToken(): Observable<any> {
    return from(this.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of(null);
        }

        const expMs = token.expiresAt * 1000;
        const needsRefresh = expMs - Date.now() <= OKTA_REFRESH_THRESHOLD_MS;

        if (!needsRefresh) {
          return of(token);
        }

        if (!this.refreshInFlight$) {
          this.refreshInFlight$ = this.refreshOktaAccessToken().pipe(
            finalize(() => { this.refreshInFlight$ = null; }),
            shareReplay({ bufferSize: 1, refCount: true })
          );
        }

        return this.refreshInFlight$;
      })
    );
  }

  public isOktaTokenEndpoint(url: string): boolean {
    if (!url) {
      return false;
    }
    // Settings may not be loaded yet (e.g. the very first request fetching
    // /settings.json itself goes through this interceptor). In that case we
    // can't know the okta endpoints — treat the URL as non-okta, which also
    // short-circuits the pre-flight refresh on the caller side.
    if (!this.appService.getSettings()) {
      return false;
    }
    const { refreshToken, accessToken } = this.url;
    return url.startsWith(refreshToken) || url.startsWith(accessToken);
  }

  public async isTokenAboutToExpire() {
    const token = await this.oktaAuth.tokenManager.get('accessToken');
    if (!token) return false;
    const { exp } = OktaService.parseToken(token.accessToken);

    return (exp * 1000) - Date.now() <= OKTA_REFRESH_THRESHOLD_MS;
  }

  public static parseToken(token) {
    const [ base64Url] = token.split('.');
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    return JSON.parse(window.atob(base64));
  }

  private getHttpParams(params) {
    return Object.getOwnPropertyNames(params)
      .reduce((httpParams, prop) => httpParams.set(prop, params[prop]), new HttpParams());
  }
}
